import { Provider } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import {
  initStore,
  intervalAtom,
  granularityAtom,
  apiAccountsDetailsAtom,
} from '@src/components/usage-dashboard/atoms';
import {
  breakdownModalVisibleAtom,
  breakdownFiltersAtom,
  breakdownChartOrderAtom,
  breakdownTableOrderAtom,
  usageTypeFilterAtom,
} from '@src/components/usage-dashboard/ApiUsageBreakdown/atoms';
import {
  USAGE_TYPE_ALL,
  getBreakdownDimensions,
} from '@src/components/usage-dashboard/ApiUsageBreakdown/constants';
import { useSelector } from 'react-redux';
import { showUserNameBreakdownFeatureSelector } from '@store/common/selectors';

const DEFAULT_CHART_ORDER = ['RealtimeAPICalls', 'HistoricalAPICalls'];
import { StyledDrawer } from './styled';
import { ErrorBoundary } from '@src/components/common';
import ApiUsageBreakdownDrawer from '@src/components/usage-dashboard/ApiUsageBreakdown/ApiUsageBreakdownDrawer';

const ApiUsageBreakdownPage = () => {
  const eventName = 'open-api-usage-breakdown';
  const [openDrawer, setOpenDrawer] = useState(false);
  const [hasContext, setHasContext] = useState(false);
  const showUserName = useSelector(showUserNameBreakdownFeatureSelector);

  const store = useMemo(() => {
    return initStore();
  }, []);

  // Read inside the effect via a ref-like closure over the latest flag value, so the
  // table order picked up on open reflects the flag without re-registering the listener.
  const defaultTableOrder = useMemo(
    () => getBreakdownDimensions(showUserName).map((dim) => dim.key),
    [showUserName],
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      // Reset breakdown-specific state on each open
      store.set(breakdownFiltersAtom, []);
      store.set(usageTypeFilterAtom, USAGE_TYPE_ALL);
      store.set(breakdownChartOrderAtom, DEFAULT_CHART_ORDER);
      store.set(breakdownTableOrderAtom, defaultTableOrder);

      // If opened from "More Info" button, seed store with dashboard context
      if (detail?.interval) {
        store.set(intervalAtom, detail.interval);
      }
      if (detail?.granularity) {
        store.set(granularityAtom, detail.granularity);
      }
      if (detail?.apiAccounts) {
        store.set(apiAccountsDetailsAtom, detail.apiAccounts);
      }

      setHasContext(!!detail?.apiAccounts);
      store.set(breakdownModalVisibleAtom, true);
      setOpenDrawer(true);
    };

    window.addEventListener(eventName, handler);
    return () => {
      window.removeEventListener(eventName, handler);
    };
  }, [defaultTableOrder]);

  const handleClose = () => {
    store.set(breakdownModalVisibleAtom, false);
    setOpenDrawer(false);
  };

  return (
    <div data-ifc-event={eventName}>
      <Provider store={store}>
        <StyledDrawer
          open={openDrawer}
          onClose={handleClose}
          zIndex={1010}
          styles={{
            wrapper: {
              width: '85%',
              minWidth: '800px',
            },
            body: {
              padding: 0,
            },
          }}
        >
          <ErrorBoundary>
            <ApiUsageBreakdownDrawer hasContext={hasContext} />
          </ErrorBoundary>
        </StyledDrawer>
      </Provider>
    </div>
  );
};

export default ApiUsageBreakdownPage;
